import { useCallback, useContext } from 'react';
import createDataContext from '../services/createDataContext';
import { mapLookupTypes } from '../services/utils';

import {
	getAgencyTypes,
	getAgencyCoordinationOptions,
	getServices,
	getCounties
} from '../api/nora/lookups';

import agenciesReducer, {
	SET_COORDINATION,
	SET_COORDINATION_OPTIONS,
	SET_AGENCY,
	SET_AGENCY_LIST,
	SET_AGENCY_LOCATIONS,
	SET_AGENCY_TYPES,
	SET_AGENCY_STAFF,
	SET_COUNTY_OPTIONS,
	SET_SERVICE_OPTION_GROUPS,
	SET_SERVICE_OPTIONS,
	SET_TABLE_STATE
} from '../reducers/agenciesReducer';

export let controller = new AbortController();

const initialState = {
	agency: null,
	agencyList: [],
	agencyTypes: [],
	coordinationOptions: [],
	countyOptions: [],
	serviceOptions: [],
	serviceOptionGroups: [],
	tableState: {
		filters: [],
		pageIndex: 0,
		pageSize: 10,
		selectedIds: [],
		sortBy: []
	}
};

const setAgencyTypes = async dispatch => {
	try {
		const [ { types } ] = await getAgencyTypes( controller.signal );

		const mappedTypes = mapLookupTypes( types );

		SET_AGENCY_TYPES( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const setCountyOptions = async dispatch => {
	try {
		const [ { types } ] = await getCounties( controller.signal );

		const mappedTypes = mapLookupTypes( types );

		SET_COUNTY_OPTIONS( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const setServiceOptions = async dispatch => {
	try {
		const data = await getServices( controller.signal );

		const optionGroups = [];

		const options = data.reduce( ( acc, { description, icon, types } ) => {
			const optionIds = types.map( ( { id } ) => id );

			optionGroups.push( {
				label: description,
				icon,
				optionIds
			} );

			const options = mapLookupTypes( types );

			return [ ...acc, ...options ];
		}, [] );

		SET_SERVICE_OPTIONS( dispatch, controller.signal )( options );
		SET_SERVICE_OPTION_GROUPS( dispatch, controller.signal )( optionGroups );
	} catch ( error ) {
		// handle
	}
};

const onInit = async dispatch => {
	controller = new AbortController();

	await setAgencyTypes( dispatch );
	await setCountyOptions( dispatch );
	await setServiceOptions( dispatch );
};

const onCleanup = () => {
	controller.abort();
};

export const {
	StateContext,
	ActionContext,
	Provider
} = createDataContext( {
	reducer: agenciesReducer,
	initialState,
	controller,
	onInit,
	onCleanup
} );

export function useAgencyState() {
	const state = useContext( StateContext );

	return state;
}

export function useAgencyAction() {
	const dispatch = useContext( ActionContext );

	const clearAgency = useCallback( () => {
		SET_AGENCY( dispatch, controller.signal )( null );
	}, [ dispatch ] );

	const setAgency = useCallback( async data => {
		await SET_AGENCY( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setAgencyList = useCallback( data => {
		SET_AGENCY_LIST( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setAgencyLocations = useCallback( async data => {
		await SET_AGENCY_LOCATIONS( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setCoordination = useCallback( data => {
		SET_COORDINATION( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setCoordinationOptions = useCallback( async agencyID => {
		try {
			const [ { types } ] = await getAgencyCoordinationOptions( agencyID );

			const mappedCoordinationOptions = types.map( ( {
				id,
				name
			} ) => ( {
				key: id,
				text: name,
				value: id
			} ) );

			SET_COORDINATION_OPTIONS( dispatch, controller.signal )( mappedCoordinationOptions );
		} catch ( error ) {
			// handle
		}
	}, [ dispatch ] );

	const setAgencyStaff = useCallback( data => {
		SET_AGENCY_STAFF( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setTableState = useCallback( updateFunc => {
		SET_TABLE_STATE( dispatch, controller.signal )( updateFunc );
	}, [ dispatch ] );

	return {
		clearAgency,
		setAgency,
		setAgencyList,
		setAgencyLocations,
		setCoordination,
		setCoordinationOptions,
		setAgencyStaff,
		setTableState
	};
}
